
























import { Component, Prop, Vue } from "vue-property-decorator";
import _ from "@/boot/lodash";

import {
  IFilter,
  IFiltersOptgroup,
  IFiltersOptions,
  IFiltersMultiSelectOption,
  IOperator
} from "@/models/tables";
import {
  ApiOperators,
  FilterOperators,
  FilterTypes,
  FILTER_OPERATORS,
  RELATIVE_DATE_OPERATORS,
  OPERATORS_ALLOWED_TO_HAVE_SUBFILTERS,
  SUBFILTER_CONCAT_CHAR
} from "@/data/table";
import { getOperatorFromValueAndApiOperator } from "@/helpers/table";
import { ITag } from "@/models/tags";

@Component
export default class ActiveFilter extends Vue {
  @Prop({ default: false })
  public isLocked!: boolean;

  @Prop({ required: true })
  public filter!: IFilter;

  public operators: IOperator[] = FILTER_OPERATORS();

  public get isDateFilter() {
    return this.filter.type === FilterTypes.DATE;
  }

  public get isMonthFilter() {
    return this.filter.type === FilterTypes.MONTH;
  }

  public get isSelectCheckboxFilter() {
    return this.filter.type === FilterTypes.SELECT_CHECKBOX;
  }

  public get isSelectRadioFilter() {
    return this.filter.type === FilterTypes.SELECT_RADIO;
  }

  public get isTagFilter() {
    return this.filter.type === FilterTypes.TAG;
  }

  public get isSelectFilter() {
    return this.filter.type === FilterTypes.SELECT;
  }

  public get isMultiSelectFilter() {
    return this.filter.type === FilterTypes.SELECT_MULTI;
  }

  public get isCategoryDataSelectorFilter() {
    return this.filter.type === FilterTypes.CATEGORY_DATA_SELECTOR;
  }

  public get isRelativeDataOperator() {
    return RELATIVE_DATE_OPERATORS.includes(this.filter.operator);
  }

  public get operator() {
    if (this.isDateFilter && this.isRelativeDataOperator) {
      return "";
    }

    const operator = this.getOperatorByKey(this.filter.operator);
    let label = operator.label;

    if (
      (this.isTagFilter || this.isMultiSelectFilter) &&
      [ApiOperators.EQUAL, ApiOperators.DEFAULT].includes(operator.apiOperator)
    ) {
      label = this.$t("_.matches_any");
    }

    return (label as string).toString().toLowerCase();
  }

  public get formattedLabel() {
    if (this.filter.value === "null")
      return `${this.filter.label} ${this.$t("_.not_set")
        .toString()
        .toLowerCase()}`.trim();

    if (this.hasSubFilters) {
      return this.filter.label;
    }
    return `${this.filter.label} ${this.operator}:`.trim();
  }

  public formatSelectCheckboxValue() {
    return _.uniq(
      _.flatten([this.filter.value]).map(value => {
        const option = _.find(this.filter.options, option => {
          const optVal = _.get(option, "value");

          if (_.isArray(optVal) && _.isArray(value))
            return _.isEqual(optVal, value);

          if (_.isArray(optVal)) return optVal.includes(value);

          const filterValue = value as string;

          return (
            (["false", "true"].includes(filterValue)
              ? optVal === (filterValue === "true")
              : false) ||
            (_.isNumber(+filterValue) ? optVal === +filterValue : false) ||
            optVal === filterValue
          );
        });

        return this.translateOptionLabel(_.get(option, "label", value));
      })
    )
      .join(", ")
      .replace(/,([^,]*)$/, ` ${(this.$t("_.or") as string).toLowerCase()} $1`); // replace last `,` with `or`
  }

  public formatDateValue() {
    if (this.isRelativeDataOperator) {
      return this.createRelativeDateMsg();
    }
    return this.$moment
      .utc(this.filter.value as string)
      .local()
      .format(this.$t("_datetime.datetime.middle") as string);
  }

  public formatMonthValue() {
    return this.$moment
      .utc(this.filter.value as string)
      .local()
      .format(this.$t("_datetime.month.long") as string);
  }

  public findFilterValue() {
    let searchedOption: any = {};
    const filterValue = this.filter.value as string;

    // Find if boolean;
    if (["false", "true"].includes(filterValue)) {
      const searchedValue = _.find(this.filter.options, [
        "value",
        filterValue === "true"
      ]);

      if (!_.isUndefined(searchedValue)) {
        searchedOption = searchedValue;
      }
    }

    // Find if numbers
    if (_.isNumber(+filterValue)) {
      const searchedValue = _.find(this.filter.options, [
        "value",
        +filterValue
      ]);

      if (!_.isUndefined(searchedValue)) {
        searchedOption = searchedValue;
      }
    }

    // Find if string
    const searchedValue = _.find(this.filter.options, ["value", filterValue]);

    if (!_.isUndefined(searchedValue)) {
      searchedOption = searchedValue;
    }

    return searchedOption;
  }

  public formatSelectValue() {
    if (!_.has(this.filter.options, "0.options"))
      return this.translateOptionLabel(
        _.get(this.findFilterValue(), "label", "")
      );

    const filterValue = this.filter.value as string;

    let selectedOption: IFiltersOptions | null = null;
    _.forEach(this.filter.options as IFiltersOptgroup[], optgroup => {
      if (selectedOption !== null) {
        return false;
      }

      _.forEach(optgroup.options as IFiltersOptions[], option => {
        if (selectedOption !== null) {
          return false;
        }

        if (
          (["false", "true"].includes(filterValue)
            ? option.value === (filterValue === "true")
            : false) ||
          (_.isNumber(+filterValue) ? option.value === +filterValue : false) ||
          option.value === filterValue
        ) {
          selectedOption = option;
        }
      });
    });

    return this.translateOptionLabel(_.get(selectedOption, "label", ""));
  }

  public translateOptionLabel(label: string) {
    return label === ""
      ? this.filter.value
      : _.startsWith(label, "_")
      ? this.$t(label)
      : label;
  }

  public formatSelectRadioValue() {
    const optionIndex = _.findIndex(
      _.get(this.filter, "options", []) as IFiltersOptions[],
      (option: IFiltersOptions) =>
        option.value.toString() === this.filter.value ||
        option.value === this.filter.value
    );

    if (optionIndex === -1) {
      return this.$t(_.toNumber(this.filter.value) ? "_.yes" : "_.no");
    }

    const optionLabel = _.get(this.filter, `options.${optionIndex}.label`);

    return this.$t(optionLabel);
  }

  public get formattedValue() {
    if (this.filter.value === "null") return null;

    let value: any = "";

    if (this.isSelectCheckboxFilter) {
      value = this.formatSelectCheckboxValue();
    } else if (this.isDateFilter) {
      value = this.formatDateValue();
    } else if (this.isMonthFilter) {
      value = this.formatMonthValue();
    } else if (this.isSelectFilter) {
      value = this.formatSelectValue();
    } else if (this.isSelectRadioFilter) {
      value = this.formatSelectRadioValue();
    } else if (this.isMultiSelectFilter) {
      value = this.formatMultiSelectValue();
    } else if (this.isCategoryDataSelectorFilter) {
      value = this.formatDataWithCategoriesValue();
    } else if (this.isTagFilter) {
      value = this.formatTagValue();
    } else if (this.hasSubFilters) {
      value = this.formatValueWithSubFilters();
    } else {
      value = (this.filter.value as string).replace(/%/g, "");
    }

    return `'${value}'`;
  }

  public formatValueWithSubFilters() {
    const subFilterValues = (this.filter.value as string).split(
      SUBFILTER_CONCAT_CHAR
    );

    const formattedValue: string[] = [];
    _.forEach(subFilterValues, value => {
      const operatorType = getOperatorFromValueAndApiOperator(
        value,
        ApiOperators.LIKE
      );
      const operator = this.getOperatorByKey(operatorType);
      formattedValue.push(
        `${(operator.label as string)
          .toString()
          .toLowerCase()}: <strong>'${value.replace(/%/g, "")}'</strong>`
      );
    });

    return formattedValue.join(
      ` ${this.$t("_.or")
        .toString()
        .toLowerCase()} `
    );
  }

  public getOperatorByKey(filterOperator: FilterOperators): IOperator {
    return _.find(this.operators, {
      key: filterOperator
    }) as IOperator;
  }

  public get hasSubFilters() {
    return (
      OPERATORS_ALLOWED_TO_HAVE_SUBFILTERS.includes(this.filter.operator) &&
      (this.filter.value as string[]).includes(SUBFILTER_CONCAT_CHAR)
    );
  }

  public formatTagValue() {
    const value: (string | number)[] = _.get(
      this.filter,
      "value",
      []
    ) as string[];
    const selectedTags = _.filter(this.filter.options as ITag[], (tag: ITag) =>
      value.includes(tag.id)
    );
    return selectedTags.map(tag => tag.name).join(", ");
  }

  public formatMultiSelectValue() {
    const value: (string | number)[] = _.get(
      this.filter,
      "value",
      []
    ) as string[];

    const selectedOptions = _.filter(
      this.flatOptions(
        _.get(this.filter, "options", []) as IFiltersMultiSelectOption[]
      ),
      option =>
        value.includes(option.id) || value.includes(option.id.toString())
    );

    return selectedOptions.map(option => option.label).join(", ");
  }

  public formatDataWithCategoriesValue() {
    const value: (string | number)[] = _.get(
      this.filter,
      "value",
      []
    ) as string[];

    const selectedOptions = _.filter(
      this.flatOptions(
        _.get(this.filter, "options", []) as IFiltersMultiSelectOption[],
        true
      ),
      option =>
        value.includes(option.value) || value.includes(option.value.toString())
    );

    return selectedOptions.map(option => option.label).join(", ");
  }

  public flatOptions(options, withoutParent = false) {
    let result = [] as IFiltersMultiSelectOption[];

    _.forEach(options, option => {
      if (!withoutParent) result.push(option);
      if (Array.isArray(option.children || option.options)) {
        result = result.concat(
          this.flatOptions(option.children || option.options)
        );
      }
    });

    return result;
  }

  public createRelativeDateMsg() {
    const value = this.filter.value as string;
    const dateTime: string = _.get(value, "0", "-");
    const underscoreIndex = value.indexOf("_");
    const dateNumber: string = value.substring(1, underscoreIndex);
    const datePeriod: string = value.substring(underscoreIndex + 1);
    const label =
      this.filter.operator === FilterOperators.BEFORE_RELATIVE
        ? `_.${dateTime === "-" ? "more_than_ago" : "until_from_now"}`
        : `_.${dateTime === "-" ? "from_ago" : "more_than_from_now"}`;

    return this.$t(label, {
      object: this.$tc(`_plurals.${datePeriod}`, +dateNumber).toLowerCase()
    });
  }
}
